/**
 * MyChannelEarningsPage — creator-side dashboard for users who own approved
 * paid channels. Premium hero with KPIs, per-channel cards with tier
 * breakdown, chain breakdown, and full payout history with tx-hash links.
 *
 * Hidden state for users who don't own any paid channels yet — clear empty
 * state explaining how to apply for monetization.
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  DollarSign, Loader2, TrendingUp, Users, Wallet, ExternalLink, Crown,
  Sparkles, ChevronRight, Calendar, Award, Receipt, BarChart3,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getMyChannelEarnings } from '../services/api';

const EXPLORERS = {
  base:     { url: 'https://basescan.org',    icon: '🔵', name: 'Base' },
  polygon:  { url: 'https://polygonscan.com', icon: '🟣', name: 'Polygon' },
  arbitrum: { url: 'https://arbiscan.io',     icon: '🔷', name: 'Arbitrum' },
  ethereum: { url: 'https://etherscan.io',    icon: '⟠', name: 'Ethereum' },
};

export default function MyChannelEarningsPage() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mount-only loader visibility (max 5s)
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);

  // Race-safe fetch
  const latestFetchRef = useRef(0);

  useEffect(() => {
    const fetchId = ++latestFetchRef.current;
    const isCurrent = () => fetchId === latestFetchRef.current;

    getMyChannelEarnings()
      .then(d => {
        if (!isCurrent()) return;
        setChannels(d?.channels || []);
      })
      .catch(e => { if (isCurrent()) setError(e.message); })
      .finally(() => { if (isCurrent()) setLoading(false); });
  }, []);

  const totals = useMemo(() => channels.reduce((acc, ch) => ({
    activeSubs: acc.activeSubs + (ch.activeSubs || 0),
    mrr: acc.mrr + (ch.mrr || 0),
    totalEarnings: acc.totalEarnings + (ch.totalEarnings || 0),
    payouts: acc.payouts + (ch.recentPayouts?.length || 0),
    projectedAnnual: acc.projectedAnnual + (ch.projectedAnnual || 0),
    platformFee: acc.platformFee + (ch.platformFeeTotal || 0),
  }), { activeSubs: 0, mrr: 0, totalEarnings: 0, payouts: 0, projectedAnnual: 0, platformFee: 0 }), [channels]);

  // ─── Empty / error / loading paths ───────────────────────────────────
  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 animate-fade-in">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-6 text-red-400">{error}</div>
      </div>
    );
  }

  if (!loading && channels.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-8 animate-fade-in">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/30">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">My Channel Earnings</h1>
              <p className="text-sm text-gray-400 mt-1">You don't have a paid channel yet — let's fix that.</p>
            </div>
          </div>
          <div className="bg-dark-700/40 rounded-2xl p-5 border border-dark-700/50">
            <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-3">How monetization works</p>
            <div className="space-y-2.5 text-sm text-gray-300">
              <Step n={1} text="Create a custom channel from Discover Channels" />
              <Step n={2} text='Click the "💰 Monetize" button in the channel header' />
              <Step n={3} text="Set 1–3 subscription tiers ($1–$500 per month)" />
              <Step n={4} text="Submit for admin approval (typical: 24–48 hours)" />
              <Step n={5} text="Subscribers pay USDC → you keep 80% on-chain, instantly" />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Link to="/community/channels"
              className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-dark-900 text-sm font-bold flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all hover:-translate-y-0.5">
              <Sparkles className="w-4 h-4" /> Browse Channels
            </Link>
            <Link to="/community"
              className="flex-1 px-5 py-3 rounded-xl bg-dark-700 hover:bg-dark-600 text-gray-300 text-sm font-bold border border-dark-600 flex items-center justify-center gap-2 transition-all">
              Open Community <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Premium hero ────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/30">
            <DollarSign className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              My Channel Earnings
              <Sparkles className="w-5 h-5 text-amber-400" />
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Live subscriber count, MRR, and on-chain payout history for the paid channels you run.
            </p>
          </div>
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <KPI icon={Users}        label="Active Subs"        value={totals.activeSubs.toLocaleString()}
            sub={`across ${channels.length} channel${channels.length !== 1 ? 's' : ''}`} color="emerald" />
          <KPI icon={TrendingUp}   label="Monthly Recurring"  value={`$${totals.mrr.toFixed(2)}`}
            sub="your 80% share" color="violet" />
          <KPI icon={Award}        label="Projected Annual"   value={`$${totals.projectedAnnual.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            sub="at current MRR" color="amber" />
          <KPI icon={Receipt}      label="Lifetime Earnings"  value={`$${totals.totalEarnings.toFixed(2)}`}
            sub={`${totals.payouts} payouts`} color="cyan" />
        </div>
      </div>

      {/* ─── Transparency strip ──────────────────────────────────────── */}
      {totals.totalEarnings > 0 && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <BarChart3 className="w-3.5 h-3.5 text-amber-400" />
            <span><span className="font-bold text-emerald-300">${totals.totalEarnings.toFixed(2)}</span> to you · <span className="font-bold text-gray-400">${totals.platformFee.toFixed(2)}</span> to platform</span>
          </div>
          <div className="flex-1 max-w-xs h-2 bg-dark-700 rounded-full overflow-hidden mx-auto">
            <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500" style={{ width: '80%' }} />
          </div>
          <span className="text-[10px] text-gray-500">80 / 20 split</span>
        </div>
      )}

      {/* ─── Initial loader ──────────────────────────────────────────── */}
      {loading && showLoader && channels.length === 0 && (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
          <span className="text-sm text-gray-400">Loading your earnings…</span>
        </div>
      )}

      {/* ─── Per-channel cards ───────────────────────────────────────── */}
      {channels.map(ch => <ChannelEarningsCard key={ch.channelId} channel={ch} />)}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════════

function KPI({ icon: Icon, label, value, sub, color }) {
  const colors = {
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-300',
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-300',
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-300',
    cyan:    'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-300',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5 border border-amber-500/30">{n}</span>
      <span>{text}</span>
    </div>
  );
}

function ChannelEarningsCard({ channel: ch }) {
  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400" />
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-dark-700 flex items-center justify-center text-2xl flex-shrink-0">
              {ch.icon || '💬'}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-white truncate flex items-center gap-2">
                {ch.name}
                <Crown className="w-4 h-4 text-amber-400" />
              </h3>
              <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{ch.description || 'Paid community channel'}</p>
            </div>
          </div>
          <div className="text-right text-[10px] text-gray-500 flex-shrink-0">
            <Calendar className="w-3 h-3 inline mr-1" /> Activated<br />
            <span className="text-gray-300 text-xs font-semibold">
              {ch.activatedAt ? new Date(ch.activatedAt).toLocaleDateString() : '—'}
            </span>
          </div>
        </div>

        {/* Channel KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-5 mb-5 border-b border-dark-700/30">
          <Stat label="Active Subs"        value={ch.activeSubs}                                   color="emerald" />
          <Stat label="Lifetime Subs"      value={ch.totalSubsLifetime}                            color="white" />
          <Stat label="MRR (your 80%)"     value={`$${(ch.mrr || 0).toFixed(2)}`}                  color="violet" />
          <Stat label="Lifetime Earned"    value={`$${(ch.totalEarnings || 0).toFixed(2)}`}        color="amber" />
        </div>

        {/* Tiers + chain breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1">
              <Award className="w-3 h-3" /> Subscription Tiers
            </p>
            <div className="space-y-1.5">
              {(ch.tiers || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between bg-dark-700/40 px-3 py-2 rounded-lg">
                  <span className="text-xs text-gray-300 font-medium">{t.name}</span>
                  <span className="text-xs text-amber-300 font-bold">${t.price}/mo</span>
                </div>
              ))}
              {(!ch.tiers || ch.tiers.length === 0) && (
                <p className="text-[10px] text-gray-600">No tiers defined</p>
              )}
            </div>
          </div>

          <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-700/50">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1">
              <BarChart3 className="w-3 h-3" /> Earnings by Chain
            </p>
            {Object.keys(ch.chainBreakdown || {}).length === 0 ? (
              <p className="text-[10px] text-gray-600">No payouts yet</p>
            ) : (
              <div className="space-y-1.5">
                {Object.entries(ch.chainBreakdown || {}).sort((a, b) => b[1] - a[1]).map(([chain, amount]) => {
                  const chainInfo = EXPLORERS[chain] || EXPLORERS.base;
                  return (
                    <div key={chain} className="flex items-center justify-between bg-dark-700/40 px-3 py-2 rounded-lg">
                      <span className="text-xs text-gray-300 inline-flex items-center gap-1.5">
                        <span>{chainInfo.icon}</span> {chainInfo.name}
                      </span>
                      <span className="text-xs text-emerald-300 font-bold">${amount.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Payout wallet */}
        <div className="mb-5 bg-dark-700/40 rounded-xl p-4 border border-dark-700/50 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <Wallet className="w-3 h-3" /> Your Payout Wallet
            </p>
            <p className="text-xs font-mono text-white truncate">
              {ch.payoutWallet
                ? `${ch.payoutWallet.slice(0, 10)}…${ch.payoutWallet.slice(-8)}`
                : <span className="text-red-400 font-sans not-italic">NOT SET — payouts paused</span>}
            </p>
          </div>
          {ch.lastPayoutAt && (
            <div className="text-right">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Last Payout</p>
              <p className="text-xs text-emerald-300 font-semibold">{new Date(ch.lastPayoutAt).toLocaleString()}</p>
              {ch.avgPayoutUsd > 0 && (
                <p className="text-[9px] text-gray-500 mt-0.5">avg ${ch.avgPayoutUsd.toFixed(2)}</p>
              )}
            </div>
          )}
        </div>

        {/* Payout history */}
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Receipt className="w-3 h-3" /> Recent Payouts ({(ch.recentPayouts || []).length})
          </p>
          {(ch.recentPayouts || []).length === 0 ? (
            <div className="bg-dark-700/30 rounded-xl p-5 text-center border border-dark-700/50">
              <Receipt className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-xs text-gray-400">No payouts yet</p>
              <p className="text-[10px] text-gray-600 mt-1">Once a subscriber pays, your 80% lands here within seconds.</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
              {ch.recentPayouts.map((p, i) => {
                const explorer = EXPLORERS[p.chain || 'base'] || EXPLORERS.base;
                return (
                  <div key={i} className="bg-dark-700/40 rounded-lg px-3 py-2 flex items-center justify-between gap-2 hover:bg-dark-700/60 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-lg">{explorer.icon}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-emerald-300">+${p.amountUsd.toFixed(2)} USDC</p>
                        <p className="text-[10px] text-gray-500">{new Date(p.paidAt).toLocaleString()} · {explorer.name}</p>
                      </div>
                    </div>
                    {p.txHash && (
                      <a href={`${explorer.url}/tx/${p.txHash}`} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] text-amber-300 hover:text-amber-200 flex items-center gap-1 font-mono px-2 py-1 rounded bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/20 flex-shrink-0">
                        {p.txHash.slice(0, 6)}…{p.txHash.slice(-4)}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const colorClass = {
    emerald: 'text-emerald-300',
    white:   'text-white',
    violet:  'text-violet-300',
    amber:   'text-amber-300',
  }[color];
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-bold ${colorClass}`}>{value}</p>
    </div>
  );
}
